import React, { useState, useEffect, useCallback } from 'react';
import { ProductProps } from '../../components/ui/ProductCard';
import { productsAPI, uploadAPI, categoriesAPI } from '../../services/api';
import { message } from 'antd';
import { Category } from '../../types/category';

// Extended ProductProps to include categories
interface ProductWithCategories extends ProductProps {
  categories?: string[];
}

interface ProductManagerProps {
  products: ProductProps[];
  setProducts: React.Dispatch<React.SetStateAction<ProductProps[]>>;
}

// Define the product form values type
interface ProductFormValues {
  id: string;
  name: string;
  productCode: string;
  image: string;
  description: string;
  price: number | string;
  specs: Array<{ label: string; value: string }>;
  categoryIds?: string[];
}

const emptyProductForm: ProductFormValues = {
  id: '',
  name: '',
  productCode: '',
  image: '',
  description: '',
  price: '',
  specs: [{ label: '', value: '' }],
  categoryIds: ['all']
};

const ProductManager: React.FC<ProductManagerProps> = ({ products, setProducts }) => {
  const [formValues, setFormValues] = useState<ProductFormValues>(emptyProductForm);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<ProductProps[]>([]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await productsAPI.getAll();
      setProducts(response.data.data);
      setFilteredProducts(response.data.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      message.error('Failed to fetch products');
    }
  }, [setProducts]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      message.error('Failed to fetch categories');
    }
  }, []);

  // Fetch products and categories from API on component mount
  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts, fetchCategories]);

  // Filter products based on search term
  useEffect(() => {
    if (Array.isArray(products)) {
      const filtered = products.filter(product => 
        (product?.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (product?.productCode?.toLowerCase() || '').includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts([]);
    }
  }, [products, searchTerm]);

  const handleAddNewProduct = () => {
    setIsEditing(false);
    setFormValues({
      ...emptyProductForm,
      id: Date.now().toString(),
    });
    setModalOpen(true);
    setImagePreview(null);
    setErrorMessage(null);
  };

  const handleEditProduct = (product: ProductProps) => {
    setIsEditing(true);
    setFormValues({
      ...product,
      id: product.id || '',
      name: product.name || '',
      productCode: product.productCode || '',
      image: product.image || '',
      description: product.description || '',
      price: product.price ? product.price.toString() : '',
      specs: product.specs && product.specs.length > 0 
        ? product.specs.map(spec => ({ 
            label: spec.label || '', 
            value: spec.value || '' 
          }))
        : [{ label: '', value: '' }],
      categoryIds: (product as ProductWithCategories).categories || ['all']
    });
    setImagePreview(product.image || null);
    setModalOpen(true);
    setErrorMessage(null);
  };

  const handleDeleteProduct = async (productId: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      setIsLoading(true);
      setErrorMessage(null);
      try {
        await productsAPI.delete(productId);
        setProducts(products.filter(p => p.id !== productId));
      } catch (error: any) {
        console.error('Error deleting product:', error);
        setErrorMessage(error.response?.data?.error?.message || 'Failed to delete product');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('File size exceeds 5MB limit. Please choose a smaller image.');
      return;
    }

    // Check file type
    if (!file.type.match('image.*')) {
      setErrorMessage('Please select an image file (JPEG, PNG, etc.)');
      return;
    }

    setIsUploading(true);
    setErrorMessage(null);

    try {
      // Set local preview immediately for better UX
      const localPreview = URL.createObjectURL(file);
      setImagePreview(localPreview);

      // Upload the image to the server
      const response = await uploadAPI.uploadImage(file);

      const imageUrl =
        response?.data?.data?.url ||
        response?.data?.url ||
        (typeof response?.data === 'string' ? response.data : null);

      if (!imageUrl) {
        setErrorMessage('Received invalid response from server. Image may not be saved properly.');
        return;
      }

      setImagePreview(imageUrl);
      setFormValues({ ...formValues, image: imageUrl });
    } catch (error: any) {
      console.error('Image upload error:', error);

      const errorMessage = 
        error.response?.data?.error?.message || 
        error.response?.data?.message || 
        error.message || 
        'Failed to upload image';
      
      setErrorMessage(`Image upload failed: ${errorMessage}`);

      const localPreview = URL.createObjectURL(file);
      setImagePreview(localPreview);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSpecChange = (index: number, field: 'label' | 'value', value: string) => {
    const updatedSpecs = [...formValues.specs];
    updatedSpecs[index] = { ...updatedSpecs[index], [field]: value };
    setFormValues({ ...formValues, specs: updatedSpecs });
  };

  const addSpec = () => {
    setFormValues({
      ...formValues,
      specs: [...formValues.specs, { label: '', value: '' }]
    });
  };

  const removeSpec = (index: number) => {
    if (formValues.specs.length > 1) {
      const updatedSpecs = formValues.specs.filter((_, i) => i !== index);
      setFormValues({ ...formValues, specs: updatedSpecs });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      const productId = formValues.id;
      const productData = {
        name: formValues.name.trim(),
        productCode: formValues.productCode.trim(),
        image: formValues.image.trim(),
        description: formValues.description.trim(),
        price: typeof formValues.price === 'string' 
          ? parseFloat(formValues.price) 
          : formValues.price,
        specs: formValues.specs
          .map((spec) => ({ label: spec.label.trim(), value: spec.value.trim() }))
          .filter((spec) => spec.label && spec.value),
        categoryIds: formValues.categoryIds || []
      };

      if (isEditing) {
        const response = await productsAPI.update(productId, productData);
        if (response.data && Array.isArray(products)) {
          const updatedProduct = response.data.data || response.data;
          setProducts(products.map(p => p.id === productId ? updatedProduct : p));
        }
      } else {
        const response = await productsAPI.create(productData);
        if (response.data && Array.isArray(products)) {
          const newProduct = response.data.data || response.data;
          setProducts([...products, newProduct]);
        }
      }

      setModalOpen(false);
      setFormValues(emptyProductForm);
      setImagePreview(null);
      message.success(isEditing ? 'Product updated successfully' : 'Product added successfully');
    } catch (error: any) {
      console.error('Error saving product:', error);
      setErrorMessage(error.response?.data?.error?.message || 'Failed to save product');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      {errorMessage && (
        <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
          <p className="font-medium">Error</p>
          <p>{errorMessage}</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <h2 className="text-xl font-heading font-bold text-text mb-4 sm:mb-0">Product Management</h2>
        <div className="flex flex-col sm:flex-row w-full sm:w-auto space-y-3 sm:space-y-0 sm:space-x-3">
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <button
            onClick={handleAddNewProduct}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors flex items-center whitespace-nowrap"
            disabled={isLoading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Product
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && !modalOpen && (
        <div className="text-center py-12">
          <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading products...</p>
        </div>
      )}

      {/* Product List */}
      {!isLoading && filteredProducts.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProducts.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-12 w-12 rounded-md overflow-hidden bg-gray-100">
                      <img 
                        src={product.image} 
                        alt={product.name} 
                        className="h-full w-full object-cover"
                        crossOrigin="anonymous"
                        onError={(e) => {
                          console.error('Product image failed to load:', product.image);
                          (e.target as HTMLImageElement).src = 'https://placehold.co/150?text=No+Image';
                        }}
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{product.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{product.productCode}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {new Intl.NumberFormat('en-ZA', {
                        style: 'currency',
                        currency: 'ZAR',
                      }).format(product.price)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEditProduct(product)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                      disabled={isLoading}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteProduct(product.id)}
                      className="text-red-600 hover:text-red-900"
                      disabled={isLoading}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !isLoading && filteredProducts.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <h3 className="text-lg font-heading font-bold text-text mb-2">No Products Found</h3>
          <p className="text-gray-500">Add a new product or try a different search term</p>
        </div>
      ) : null}

      {/* Product Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="text-lg font-heading font-bold text-text">
                {isEditing ? 'Edit Product' : 'Add New Product'}
              </h3>
              <button 
                onClick={() => setModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
                disabled={isLoading || isUploading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              {errorMessage && (
                <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
                  <p>{errorMessage}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div>
                    <label htmlFor="productName" className="block text-sm font-medium text-gray-700 mb-1">
                      Product Name *
                    </label>
                    <input
                      type="text"
                      id="productName"
                      value={formValues.name}
                      onChange={(e) => setFormValues({...formValues, name: e.target.value})}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      disabled={isLoading}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="productCode" className="block text-sm font-medium text-gray-700 mb-1">
                      Product Code *
                    </label>
                    <input
                      type="text"
                      id="productCode"
                      value={formValues.productCode}
                      onChange={(e) => setFormValues({...formValues, productCode: e.target.value})}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      disabled={isLoading}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                      Price (ZAR) *
                    </label>
                    <input
                      type="number"
                      id="price"
                      value={formValues.price}
                      onChange={(e) => setFormValues({...formValues, price: e.target.value})}
                      step="0.01"
                      min="0"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      disabled={isLoading}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      id="category"
                      value={formValues.categoryIds?.[0] || 'all'}
                      onChange={(e) => setFormValues({...formValues, categoryIds: [e.target.value]})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      disabled={isLoading}
                    >
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                      Description *
                    </label>
                    <textarea
                      id="description"
                      value={formValues.description}
                      onChange={(e) => setFormValues({...formValues, description: e.target.value})}
                      required
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                      disabled={isLoading}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-1">
                      Product Image
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 h-24 w-24 border border-gray-200 rounded-md overflow-hidden bg-gray-100">
                        {imagePreview ? (
                          <img
                            src={imagePreview}
                            alt="Product Preview"
                            className="h-full w-full object-cover"
                            crossOrigin="anonymous"
                            onError={(e) => {
                              console.error('Image failed to load:', imagePreview);
                              (e.target as HTMLImageElement).src = 'https://placehold.co/150?text=No+Image';
                            }}
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full w-full text-gray-400">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex-grow">
                        <input
                          type="file"
                          id="image"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                          disabled={isLoading || isUploading}
                        />
                        <label
                          htmlFor="image"
                          className={`px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 cursor-pointer inline-block transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {isUploading ? 'Uploading...' : 'Choose Image'}
                        </label>
                        {/* Allow direct URL input as well */}
                        <input
                          type="text"
                          placeholder="Or paste image URL"
                          value={formValues.image}
                          onChange={(e) => {
                            setFormValues({...formValues, image: e.target.value});
                            setImagePreview(e.target.value);
                          }}
                          className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                          disabled={isLoading || isUploading}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Specifications */}
              <div className="mt-8">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-heading font-semibold text-gray-700">Specifications</h4>
                  <button
                    type="button"
                    onClick={addSpec}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-sm flex items-center"
                    disabled={isLoading}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Spec
                  </button>
                </div>
                
                {formValues.specs.map((spec, index) => (
                  <div key={index} className="flex space-x-3 mb-3">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Specification (e.g. Weight)"
                        value={spec.label}
                        onChange={(e) => handleSpecChange(index, 'label', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        disabled={isLoading}
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Value (e.g. 2.5kg)"
                        value={spec.value}
                        onChange={(e) => handleSpecChange(index, 'value', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        disabled={isLoading}
                      />
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => removeSpec(index)}
                        disabled={formValues.specs.length <= 1 || isLoading}
                        className={`h-full px-3 rounded ${
                          formValues.specs.length > 1 && !isLoading
                            ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        } transition-colors`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  disabled={isLoading || isUploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || isUploading}
                  className={`px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors flex items-center ${
                    isLoading || isUploading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isLoading && (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {isEditing ? 'Update Product' : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManager; 